import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LinearProgress } from '@mui/material';
import SABIN_STRAINS from '../assets/HumanPoliovirusStrain/SABIN_STRAINS.js';

// ── Design constants ──────────────────────────────────────────────────────────

const BASE_COLORS = {
  standard: {
    A: '#9333ea', C: '#22c55e', G: '#3b82f6', T: '#ef4444', U: '#ef4444',
    N: '#475569', '-': '#d1d5db',
  },
  colorblind: {
    A: '#648FFF', C: '#FFB000', G: '#785EF0', T: '#FE6100', U: '#FE6100',
    N: '#626262', '-': '#D8D8D8',
  },
};

const SIMILARITY_RANGES = [
  { label: 'Strong match',   symbol: '●●●', min: 95, max: 100, bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
  { label: 'Good match',     symbol: '●●○', min: 85, max: 95,  bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
  { label: 'Moderate match', symbol: '●○○', min: 75, max: 85,  bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  { label: 'Weak match',     symbol: '○○○', min: 0,  max: 75,  bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
];

const BASES_PER_ROW_OPTIONS = [30, 60, 100, 200];
const RULER_INTERVAL = 10;
const LABEL_W = 118;

// ── Helpers ───────────────────────────────────────────────────────────────────

const getSimRange = (pct) =>
  SIMILARITY_RANGES.find(r => pct > r.min && pct <= r.max) || SIMILARITY_RANGES[SIMILARITY_RANGES.length - 1];

const getBaseColor = (base, theme = 'standard') =>
  BASE_COLORS[theme]?.[base?.toUpperCase()] ?? '#000000';

const fmt = (v) => (v === null || v === undefined || v === '') ? '—' : v;

const buildConsensus = (qseq, sseq) =>
  qseq.split('').map((q, i) =>
    q.toUpperCase() === (sseq[i] || '').toUpperCase() ? q.toUpperCase() : 'N'
  ).join('');

// ── Sabin comparison ──────────────────────────────────────────────────────────

const calcSim = (seq1, seq2) => {
  const len = Math.min(seq1.length, seq2.length);
  let m = 0;
  for (let i = 0; i < len; i++) {
    if (seq1[i].toUpperCase() === seq2[i].toUpperCase()) m++;
  }
  return (m / len) * 100;
};

const compareWithSabin = (querySeq, strains) =>
  strains.map(strain => {
    let best = 0, bestPos = 0;
    if (querySeq.length < strain.sequence.length) {
      const maxOff = strain.sequence.length - querySeq.length;
      const step = Math.max(1, Math.floor(maxOff / 500));
      for (let off = 0; off <= maxOff; off += step) {
        const s = calcSim(querySeq, strain.sequence.substring(off, off + querySeq.length));
        if (s > best) { best = s; bestPos = off; }
        if (s > 98) break;
      }
      for (let off = Math.max(0, bestPos - 10); off <= Math.min(maxOff, bestPos + 10); off++) {
        const s = calcSim(querySeq, strain.sequence.substring(off, off + querySeq.length));
        if (s > best) { best = s; bestPos = off; }
      }
    } else {
      const l = Math.min(querySeq.length, strain.sequence.length);
      best = calcSim(querySeq.substring(0, l), strain.sequence.substring(0, l));
    }
    const similarity = parseFloat(best.toFixed(2));
    return { strain: strain.name, id: strain.id, similarity, position: bestPos, rangeInfo: getSimRange(similarity) };
  }).sort((a, b) => b.similarity - a.similarity);

// ── Sub-components ────────────────────────────────────────────────────────────

function BaseTooltip({ data }) {
  if (!data) return null;
  return (
    <div style={{
      position: 'fixed', left: data.x + 20, top: data.y - 90,
      background: '#ffffff', color: '#000000',
      padding: '12px 18px', borderRadius: 10,
      fontSize: 16, fontFamily: "'Courier New', monospace",
      pointerEvents: 'none', zIndex: 9999,
      boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
      lineHeight: 2, whiteSpace: 'nowrap',
      border: '1px solid #475569',
      minWidth: 180,
    }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#000000', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
        Position {data.pos}
      </div>
      <div>
        <span style={{ color: '#000000', marginRight: 4 }}>Query :</span>
        <span style={{ fontSize: 16, fontWeight: 900 }}>{data.q}</span>
        <span style={{ color: '#000000', margin: '0 10px' }}>·</span>
        <span style={{ color: '#000000', marginRight: 4 }}>Subject :</span>
        <span style={{ fontSize: 16, fontWeight: 900 }}>{data.s}</span>
      </div>
      <div>
        <span style={{ color: '#000000', marginRight: 4 }}>Consensus :</span>
        <span style={{ fontSize: 16, fontWeight: 900 }}>{data.c}</span>
      </div>
      <div style={{
        marginTop: 6, paddingTop: 6, borderTop: '1px solid #334155',
        fontSize: 13, fontWeight: 700,
        color: data.match ? '#4ade80' : '#f87171',
      }}>
        {data.match ? '✓ match' : '✗ mismatch'}
      </div>
    </div>
  );
}

function StatusBadge({ similarity }) {
  const r = getSimRange(similarity);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 20,
      background: r.bg, color: r.text,
      border: `1px solid ${r.border}`,
      fontSize: 10.5, fontWeight: 700, letterSpacing: '0.02em',
    }}>
      <span style={{ letterSpacing: 3, fontSize: 9 }}>{r.symbol}</span>
      {r.label}
    </span>
  );
}

function MetaGrid({ alignments }) {
  const fields = [
    { label: 'Run',      value: fmt(alignments.run ?? alignments.qseqid) },
    { label: 'pident',   value: fmt(alignments.pident) },
    { label: 'qlen',     value: fmt(alignments.qlen) },
    { label: 'len',      value: fmt(alignments.len) },
    { label: 'mm',       value: fmt(alignments.mm) },
    { label: 'gap',      value: fmt(alignments.gap) },
    { label: 'qstart',   value: fmt(alignments.qstart) },
    { label: 'sstart',   value: fmt(alignments.sstart) },
    { label: 'send',     value: fmt(alignments.send) },
    { label: 'evalue',   value: fmt(alignments.evalue) },
    { label: 'bit',      value: fmt(alignments.bit) },
    { label: 'poltype',  value: fmt(alignments.poltype) },
    { label: 'polstart', value: fmt(alignments.polstart) },
    { label: 'polend',   value: fmt(alignments.polend) },
  ];
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
      gap: 1, background: '#e2e8f0',
      border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden',
    }}>
      {fields.map(({ label, value }) => (
        <div key={label} style={{
          display: 'flex', gap: 6, alignItems: 'baseline',
          padding: '5px 10px', background: '#fff',
        }}>
          <span style={{ fontSize: 9.5, fontWeight: 700, color: '#000000', minWidth: 48, flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {label}
          </span>
          <span style={{ fontSize: 11.5, fontFamily: "'Courier New', monospace", color: '#000000', fontWeight: 600 }}>
            {value}
          </span>
        </div>
      ))}
    </div>
  );
}

function SabinCard({ results, loading, qseqLen, onViewPosition }) {
  const [sortKey, setSortKey] = useState('similarity');
  const [sortDir, setSortDir] = useState('desc');

  const sorted = [...(results || [])].sort((a, b) => {
    const va = a[sortKey], vb = b[sortKey];
    if (sortDir === 'asc') return va > vb ? 1 : -1;
    return va < vb ? 1 : -1;
  });

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return <span style={{ color: '#d1d5db', marginLeft: 3 }}>↕</span>;
    return <span style={{ color: '#3b82f6', marginLeft: 3 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  if (loading) return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14 }}>
      <div style={{ fontSize: 11, color: '#000000', marginBottom: 8 }}>Analyzing VP1 similarity against Sabin reference strains…</div>
      <LinearProgress />
    </div>
  );

  if (!results?.length) return null;

  const best = sorted[0];

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      {/* Card header */}
      <div style={{
        padding: '8px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#000000', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Reference Strain Comparison — VP1 Region
        </span>
        {best && (
          <span style={{
            fontSize: 11, fontWeight: 600,
            background: best.rangeInfo.bg, color: best.rangeInfo.text,
            border: `1px solid ${best.rangeInfo.border}`,
            padding: '2px 10px', borderRadius: 10,
          }}>
            ★ Best: {best.strain.replace(', VP1 region', '')} · {best.similarity.toFixed(2)}%
          </span>
        )}
      </div>

      {/* Best match banner */}
      {best && (
        <div style={{
          padding: '8px 14px',
          background: best.rangeInfo.bg,
          borderBottom: '1px solid ' + best.rangeInfo.border,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          fontFamily: 'sans-serif', flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16 }}>★</span>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: best.rangeInfo.text }}>
                {best.rangeInfo.label} — {best.strain.replace(', VP1 region', '')}
              </div>
              <div style={{ fontSize: 11, color: best.rangeInfo.text, opacity: 0.8 }}>
                {best.similarity.toFixed(2)}% similarity · VP1 Position {best.position}–{best.position + qseqLen}
              </div>
            </div>
          </div>
          {onViewPosition && (
            <button
              onClick={() => onViewPosition({ start: best.position, end: best.position + qseqLen, strain: best.strain.replace(', VP1 region', '') })}
              style={{
                padding: '5px 14px', fontSize: 11.5, fontWeight: 700, borderRadius: 6,
                border: `1.5px solid ${best.rangeInfo.border}`,
                background: '#fff', color: best.rangeInfo.text,
                cursor: 'pointer', whiteSpace: 'nowrap',
                display: 'flex', alignItems: 'center', gap: 5,
              }}
            >
              ⊞ View Position
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            {[
              { key: 'strain',     label: 'Strain' },
              { key: 'similarity', label: 'Similarity' },
              { key: 'position',   label: 'VP1 Position' },
              { key: null,         label: 'Classification' },
            ].map(({ key, label }) => (
              <th key={label}
                onClick={() => key && toggleSort(key)}
                style={{
                  padding: '7px 12px', textAlign: 'left',
                  fontSize: 9.5, fontWeight: 700, color: '#000000',
                  textTransform: 'uppercase', letterSpacing: '0.07em',
                  cursor: key ? 'pointer' : 'default', userSelect: 'none',
                  whiteSpace: 'nowrap',
                }}>
                {label}{key ? sortIcon(key) : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => {
            const isBest = i === 0;
            return (
              <tr key={r.id} style={{
                borderBottom: '1px solid #f1f5f9',
                background: isBest ? r.rangeInfo.bg + '55' : 'transparent',
              }}>
                <td style={{ padding: '8px 12px', color: '#1e293b', fontWeight: isBest ? 700 : 400 }}>
                  {isBest && <span style={{ marginRight: 5, color: r.rangeInfo.text }}>★</span>}
                  {r.strain.replace(', VP1 region', '')}
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 9px', borderRadius: 12,
                    background: r.rangeInfo.bg, color: r.rangeInfo.text,
                    border: `1px solid ${r.rangeInfo.border}`,
                    fontSize: 11, fontWeight: 700,
                    fontFamily: "'Courier New', monospace",
                  }}>
                    {r.similarity.toFixed(2)}%
                  </span>
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#000000', fontSize: 11, fontFamily: "'Courier New', monospace" }}>
                      {r.position} – {r.position + qseqLen}
                    </span>
                    {onViewPosition && (
                      <button
                        onClick={() => onViewPosition({ start: r.position, end: r.position + qseqLen, strain: r.strain.replace(', VP1 region', '') })}
                        style={{
                          padding: '2px 8px', fontSize: 10, fontWeight: 600, borderRadius: 4,
                          border: `1px solid ${r.rangeInfo.border}`,
                          background: r.rangeInfo.bg, color: r.rangeInfo.text,
                          cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                      >
                        View ↓
                      </button>
                    )}
                  </div>
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <span style={{
                    fontSize: 10, fontWeight: 600, color: r.rangeInfo.text,
                    display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
                  }}>
                    <span style={{ letterSpacing: 2, fontSize: 9 }}>{r.rangeInfo.symbol}</span>
                    {r.rangeInfo.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ConservTrack({ qChunk, sChunk, cellW, mode }) {
  return (
    <>
      {qChunk.split('').map((q, i) => {
        const s = (sChunk[i] || '').toUpperCase();
        const match = q.toUpperCase() === s && q !== '-' && q !== 'N';

        if (mode === 'binary') {
          return (
            <span key={i} style={{
              display: 'inline-block', width: cellW, height: 10,
              background: match ? '#22c55e' : '#f1f5f9',
              borderRadius: 1,
            }} />
          );
        }
        if (mode === 'bar') {
          return (
            <span key={i} style={{
              display: 'inline-flex', alignItems: 'flex-end',
              width: cellW, height: 14,
            }}>
              <span style={{
                width: cellW - 2, height: match ? '100%' : '20%',
                background: match ? '#22c55e' : '#f87171',
                borderRadius: '2px 2px 0 0',
              }} />
            </span>
          );
        }
        // heat
        return (
          <span key={i} style={{
            display: 'inline-block', width: cellW, height: 10,
            background: match ? '#065f46' : '#fef2f2',
          }} />
        );
      })}
    </>
  );
}

function MismatchTrack({ qChunk, sChunk, cellW }) {
  return (
    <>
      {qChunk.split('').map((q, i) => {
        const s = (sChunk[i] || '').toUpperCase();
        const qU = q.toUpperCase();
        const isGap = q === '-' || sChunk[i] === '-';
        const isUnk = qU === 'N' || s === 'N';
        const isMM = !isGap && !isUnk && qU !== s;
        const bg = isGap ? '#fbbf24' : isUnk ? '#000000' : isMM ? '#ef4444' : 'transparent';
        return (
          <span key={i} style={{
            display: 'inline-block', width: cellW, height: 7,
            background: bg, borderRadius: 1,
          }} />
        );
      })}
    </>
  );
}

function SequenceViewer({ qseq, sseq, colorTheme, vp1Region }) {
  const [basesPerRow, setBasesPerRow] = useState(60);
  const [cellPx,      setCellPx]      = useState(20);
  const [conservMode, setConservMode] = useState('bar');
  const [jumpVal,     setJumpVal]     = useState('');
  const [tooltip,     setTooltip]     = useState(null);
  const [selRange,    setSelRange]     = useState(null);
  const [selAnchor,   setSelAnchor]    = useState(null);

  const consensus = buildConsensus(qseq, sseq);

  const chunks = [];
  for (let i = 0; i < qseq.length; i += basesPerRow) {
    chunks.push({ start: i, qC: qseq.slice(i, i + basesPerRow), sC: sseq.slice(i, i + basesPerRow), cC: consensus.slice(i, i + basesPerRow) });
  }

  const handleJump = () => {
    const pos = parseInt(jumpVal, 10) - 1;
    if (isNaN(pos) || pos < 0) return;
    const el = document.getElementById(`sa-chunk-${Math.floor(pos / basesPerRow)}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCopySelected = () => {
    if (!selRange) return;
    const lo = Math.min(selRange.s, selRange.e), hi = Math.max(selRange.s, selRange.e);
    navigator.clipboard?.writeText(qseq.slice(lo, hi + 1));
  };

  // Auto-scroll to the first highlighted chunk when vp1Region is set
  useEffect(() => {
    if (!vp1Region) return;
    const el = document.getElementById('sa-chunk-0');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [vp1Region]);

  const onBaseEnter = useCallback((e, absPos, q, s, c) => {
    setTooltip({ x: e.clientX, y: e.clientY, pos: absPos + 1, q: q.toUpperCase(), s: s.toUpperCase(), c: c.toUpperCase(), match: q.toUpperCase() === s.toUpperCase() });
  }, []);
  const onBaseLeave = useCallback(() => setTooltip(null), []);

  const renderBases = (seq, absStart, seqQ, seqS, seqC) =>
    seq.split('').map((base, i) => {
      const ap = absStart + i;
      const inSel = selRange && ap >= Math.min(selRange.s, selRange.e) && ap <= Math.max(selRange.s, selRange.e);
      const q = (seqQ[i] || '-'), s = (seqS[i] || '-'), c = (seqC[i] || 'N');
      const cellH = cellPx + 3;
      const fs = Math.max(7, cellPx - 5);
      return (
        <span
          key={i}
          onMouseEnter={e => onBaseEnter(e, ap, q, s, c)}
          onMouseLeave={onBaseLeave}
          onMouseDown={() => setSelAnchor(ap)}
          onMouseUp={() => { if (selAnchor !== null) { setSelRange({ s: selAnchor, e: ap }); setSelAnchor(null); } }}
          style={{
            display: 'inline-block',
            width: cellPx, height: cellH, lineHeight: `${cellH}px`,
            textAlign: 'center', fontSize: fs,
            fontFamily: "'Courier New', monospace",
            color: '#fff',
            background: inSel ? '#f59e0b' : getBaseColor(base, colorTheme),
            cursor: 'crosshair',
            userSelect: 'none',
            boxSizing: 'border-box',
            outline: inSel ? '1px solid #d97706' : 'none',
          }}
        >
          {base.toUpperCase()}
        </span>
      );
    });

  const renderLogo = (qC, sC) =>
    qC.split('').map((q, i) => {
      const s = (sC[i] || '-');
      const qU = q.toUpperCase(), sU = s.toUpperCase();
      const isMatch = qU === sU && qU !== '-' && qU !== 'N';
      const cellH = cellPx + 3;
      const fs = Math.max(7, cellPx - 5);

      if (isMatch) {
        return (
          <span key={i} style={{
            display: 'inline-block',
            width: cellPx, height: cellH, lineHeight: `${cellH}px`,
            textAlign: 'center', fontSize: fs,
            fontFamily: "'Courier New', monospace",
            color: getBaseColor(qU, colorTheme),
            fontWeight: 900,
          }}>
            {qU}
          </span>
        );
      }

      const halfH = Math.floor(cellH / 2);
      const smallFs = Math.max(5, Math.floor(cellPx * 0.55));
      return (
        <span key={i} style={{
          display: 'inline-flex', flexDirection: 'column',
          width: cellPx, height: cellH, overflow: 'hidden',
        }}>
          <span style={{
            height: halfH, lineHeight: `${halfH}px`, textAlign: 'center',
            fontSize: smallFs, fontFamily: "'Courier New', monospace",
            color: getBaseColor(qU, colorTheme), fontWeight: 700,
          }}>{qU}</span>
          <span style={{
            height: cellH - halfH, lineHeight: `${cellH - halfH}px`, textAlign: 'center',
            fontSize: smallFs, fontFamily: "'Courier New', monospace",
            color: getBaseColor(sU, colorTheme), fontWeight: 700,
          }}>{sU}</span>
        </span>
      );
    });

  const renderConsensus = (cC, qC, start) =>
    cC.split('').map((base, i) => {
      const isMatch = base.toUpperCase() !== 'N';
      const cellH = cellPx + 3;
      const fs = Math.max(7, cellPx - 5);
      return (
        <span key={i} style={{
          display: 'inline-block',
          width: cellPx, height: cellH, lineHeight: `${cellH}px`,
          textAlign: 'center', fontSize: fs,
          fontFamily: "'Courier New', monospace",
          color: isMatch ? '#fff' : '#000000',
          background: isMatch ? getBaseColor(base, colorTheme) : '#f1f5f9',
        }}>
          {isMatch ? base.toUpperCase() : '·'}
        </span>
      );
    });

  const labelStyle = {
    padding: '2px 10px',
    fontSize: 10, fontWeight: 700,
    background: '#f8fafc', borderRight: '1px solid #e2e8f0',
    whiteSpace: 'nowrap', minWidth: LABEL_W, width: LABEL_W,
    position: 'sticky', left: 0, zIndex: 2,
    fontFamily: 'sans-serif', verticalAlign: 'middle',
  };

  const selLen = selRange ? Math.abs(selRange.e - selRange.s) + 1 : 0;

  return (
    <div>
      {/* ── Viewer toolbar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', paddingBottom: 10, marginBottom: 10, borderBottom: '1px solid #f1f5f9' }}>
        {/* Bases/row */}
        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#000000', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Bases/row</span>
        {BASES_PER_ROW_OPTIONS.map(n => (
          <button key={n} onClick={() => setBasesPerRow(n)} style={{
            padding: '2px 8px', fontSize: 10.5, borderRadius: 4,
            border: `1px solid ${basesPerRow === n ? '#3b82f6' : '#e2e8f0'}`,
            background: basesPerRow === n ? '#eff6ff' : '#fff',
            color: basesPerRow === n ? '#1d4ed8' : '#000000',
            cursor: 'pointer', fontWeight: basesPerRow === n ? 700 : 400,
          }}>{n}</button>
        ))}

        <div style={{ width: 1, height: 16, background: '#e2e8f0', margin: '0 2px' }} />

        {/* Zoom */}
        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#000000', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Zoom</span>
        {[['S', 12], ['M', 15], ['L', 20]].map(([k, v]) => (
          <button key={k} onClick={() => setCellPx(v)} style={{
            padding: '2px 7px', fontSize: 10.5, borderRadius: 4,
            border: `1px solid ${cellPx === v ? '#3b82f6' : '#e2e8f0'}`,
            background: cellPx === v ? '#eff6ff' : '#fff',
            color: cellPx === v ? '#1d4ed8' : '#000000',
            cursor: 'pointer', fontWeight: cellPx === v ? 700 : 400,
          }}>{k}</button>
        ))}

        <div style={{ width: 1, height: 16, background: '#e2e8f0', margin: '0 2px' }} />

        {/* Conservation mode */}
        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#000000', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Conservation</span>
        {[['binary', 'Binary'], ['bar', 'Bar'], ['heat', 'Heatmap']].map(([k, l]) => (
          <button key={k} onClick={() => setConservMode(k)} style={{
            padding: '2px 8px', fontSize: 10.5, borderRadius: 4,
            border: `1px solid ${conservMode === k ? '#3b82f6' : '#e2e8f0'}`,
            background: conservMode === k ? '#eff6ff' : '#fff',
            color: conservMode === k ? '#1d4ed8' : '#000000',
            cursor: 'pointer', fontWeight: conservMode === k ? 700 : 400,
          }}>{l}</button>
        ))}

        <div style={{ width: 1, height: 16, background: '#e2e8f0', margin: '0 2px' }} />

        {/* Jump to position */}
        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#000000', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Jump</span>
        <input
          value={jumpVal}
          onChange={e => setJumpVal(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleJump()}
          placeholder="pos…"
          style={{
            width: 56, padding: '2px 7px',
            border: '1px solid #e2e8f0', borderRadius: 4,
            fontSize: 10.5, fontFamily: "'Courier New', monospace", outline: 'none',
          }}
        />
        <button onClick={handleJump} style={{
          padding: '2px 8px', fontSize: 10.5, borderRadius: 4,
          border: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569', cursor: 'pointer',
        }}>Go</button>

        {selLen > 0 && (
          <>
            <div style={{ width: 1, height: 16, background: '#e2e8f0', margin: '0 2px' }} />
            <button onClick={handleCopySelected} style={{
              padding: '2px 10px', fontSize: 10.5, borderRadius: 4,
              border: '1px solid #3b82f6', background: '#eff6ff',
              color: '#1d4ed8', cursor: 'pointer', fontWeight: 600,
            }}>⎘ Copy {selLen} bp</button>
            <button onClick={() => setSelRange(null)} style={{
              padding: '2px 6px', fontSize: 10.5, borderRadius: 4,
              border: '1px solid #e2e8f0', background: '#fff', color: '#000000', cursor: 'pointer',
            }}>✕</button>
          </>
        )}
      </div>

      {/* ── Nucleotide legend ── */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '4px 14px',
        padding: '6px 10px', background: '#f8fafc',
        border: '1px solid #e2e8f0', borderRadius: 6, marginBottom: 10,
        fontFamily: 'sans-serif',
      }}>
        {[
          { base: 'A', label: 'Adenine (A)' },
          { base: 'C', label: 'Cytosine (C)' },
          { base: 'G', label: 'Guanine (G)' },
          { base: 'T', label: 'Thymine (T/U)' },
          { base: 'N', label: 'Unknown (N)' },
          { base: '-', label: 'Gap (-)' },
        ].map(({ base, label }) => (
          <div key={base} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: '#475569' }}>
            <span style={{ width: 13, height: 9, borderRadius: 2, background: getBaseColor(base, colorTheme), display: 'inline-block', flexShrink: 0 }} />
            {label}
          </div>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, fontSize: 10, color: '#000000' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 7, background: '#ef4444', borderRadius: 1, display: 'inline-block' }} />
            Mismatch
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 7, background: '#fbbf24', borderRadius: 1, display: 'inline-block' }} />
            Gap
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 7, background: '#000000', borderRadius: 1, display: 'inline-block' }} />
            Unknown
          </span>
        </div>
      </div>

      {/* ── Sequence chunks ── */}
      {chunks.map(({ start, qC, sC, cC }, idx) => {
        const end = start + qC.length - 1;
        const isVp1Chunk = !!vp1Region;
        return (
          <div key={idx} id={`sa-chunk-${idx}`} style={{
            background: '#fff',
            border: isVp1Chunk ? '2px dashed #f59e0b' : '1px solid #e2e8f0',
            borderRadius: 8, overflow: 'hidden', marginBottom: 10,
            boxShadow: isVp1Chunk ? '0 0 0 3px #fef3c755' : 'none',
          }}>
            {/* VP1 region label */}
            {isVp1Chunk && (
              <div style={{
                padding: '3px 12px',
                background: '#fef3c7', borderBottom: '1px dashed #f59e0b',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                fontFamily: 'sans-serif',
              }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#92400e', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ letterSpacing: 2 }}>- - -</span>
                  VP1 Position {vp1Region.start}–{vp1Region.end} · {vp1Region.strain}
                  <span style={{ letterSpacing: 2 }}>- - -</span>
                </span>
              </div>
            )}
            {/* Chunk title */}
            <div style={{
              padding: '5px 12px', background: '#ffffff',
              color: '#000000', fontSize: 10, fontWeight: 700,
              letterSpacing: '0.05em', fontFamily: 'sans-serif',
              display: 'flex', justifyContent: 'space-between',
            }}>
              <span>Positions {start + 1} – {end + 1}</span>
              <span>{qC.length} bp · chunk {idx + 1}/{chunks.length}</span>
            </div>

            {/* Grid */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', minWidth: 'max-content' }}>
                <tbody>

                  {/* Ruler */}
                  <tr>
                    <td style={{ ...labelStyle, fontSize: 9, color: '#000000', fontWeight: 600 }}>Position</td>
                    <td style={{ padding: '2px 0', height: 14, position: 'relative' }}>
                      <div style={{ position: 'relative', width: qC.length * cellPx, height: 14 }}>
                        {qC.split('').map((_, i) => {
                          const p = start + i + 1;
                          if (i === 0 || p % RULER_INTERVAL === 0) {
                            return (
                              <span key={i} style={{
                                position: 'absolute', left: i * cellPx,
                                fontSize: 8, color: '#000000',
                                fontFamily: "'Courier New', monospace",
                                lineHeight: '14px', whiteSpace: 'nowrap',
                              }}>{p}</span>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </td>
                  </tr>

                  {/* Query */}
                  <tr>
                    <td style={{ ...labelStyle, color: '#1e40af' }}>Query (QSEQ)</td>
                    <td style={{ padding: '1px 0', lineHeight: 0 }}>
                      {renderBases(qC, start, qC, sC, cC)}
                    </td>
                  </tr>

                  {/* Subject */}
                  <tr>
                    <td style={{ ...labelStyle, color: '#065f46' }}>Subject (SSEQ)</td>
                    <td style={{ padding: '1px 0', lineHeight: 0 }}>
                      {renderBases(sC, start, qC, sC, cC)}
                    </td>
                  </tr>

                  {/* Consensus */}
                  <tr>
                    <td style={{ ...labelStyle, color: '#475569' }}>Consensus</td>
                    <td style={{ padding: '1px 0', lineHeight: 0 }}>
                      {renderConsensus(cC, qC, start)}
                    </td>
                  </tr>

                  {/* Conservation */}
                  <tr>
                    <td style={{ ...labelStyle, color: '#475569' }}>Conservation</td>
                    <td style={{ padding: '2px 0', verticalAlign: 'middle' }}>
                      <ConservTrack qChunk={qC} sChunk={sC} cellW={cellPx} mode={conservMode} />
                    </td>
                  </tr>

                  {/* Mismatch track */}
                  <tr>
                    <td style={{ ...labelStyle, color: '#000000' }}>Mismatch track</td>
                    <td style={{ padding: '2px 0', verticalAlign: 'middle' }}>
                      <MismatchTrack qChunk={qC} sChunk={sC} cellW={cellPx} />
                    </td>
                  </tr>

                  {/* Sequence Logo */}
                  <tr>
                    <td style={{ ...labelStyle, color: '#7c3aed', borderBottom: 'none' }}>Sequence Logo</td>
                    <td style={{ padding: '1px 0', lineHeight: 0, borderBottom: 'none' }}>
                      {renderLogo(qC, sC)}
                    </td>
                  </tr>

                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {tooltip && <BaseTooltip data={tooltip} />}
    </div>
  );
}

function InfoPanel() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '9px 14px', background: '#f8fafc', border: 'none', cursor: 'pointer',
          fontFamily: 'sans-serif',
        }}
      >
        <span style={{ fontSize: 10, fontWeight: 700, color: '#000000', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Sequence Alignment Guide & Legend
        </span>
        <span style={{ fontSize: 11, color: '#000000', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▼</span>
      </button>

      {open && (
        <div style={{ padding: '14px 16px', fontFamily: 'sans-serif', fontSize: 12, color: '#334155', lineHeight: 1.65 }}>
          <p style={{ margin: '0 0 12px', fontSize: 12.5 }}>
            Pairwise alignment between the SRA query (QSEQ) and the BLAST subject sequence (SSEQ),
            compared against Sabin VP1 reference sequences for poliovirus classification.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 14 }}>
            <tbody>
              {[
                ['Query (QSEQ)',     'The nucleotide sequence from the SRA run being analyzed.'],
                ['Subject (SSEQ)',   'The matching BLAST database sequence.'],
                ['Consensus',        'Colored base = match between Q and S. Dot (·) = mismatch.'],
                ['Conservation',     'Binary: green/white. Bar: height = conservation. Heatmap: dark green = conserved.'],
                ['Mismatch track',   'Red = mismatch · Yellow = insertion/deletion gap · Gray = ambiguous (N).'],
                ['Sequence Logo',    'Match: one large colored letter. Mismatch: two small stacked letters — Query (top) / Subject (bottom).'],
                ['VP1 Similarity',   'Compared against Sabin 1/2/3 VP1 region (~900 bp). VDPV threshold: >1% divergence (PV1/3), >0.6% (PV2).'],
              ].map(([term, desc]) => (
                <tr key={term} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '5px 8px 5px 0', fontWeight: 700, color: '#1e40af', fontFamily: "'Courier New', monospace", fontSize: 11, verticalAlign: 'top', width: 140, whiteSpace: 'nowrap' }}>
                    {term}
                  </td>
                  <td style={{ padding: '5px 0', color: '#475569', fontSize: 11.5 }}>{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: '#000000', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
              Match classification
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {SIMILARITY_RANGES.map(r => (
                <span key={r.label} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '3px 10px', borderRadius: 20,
                  background: r.bg, color: r.text,
                  border: `1px solid ${r.border}`,
                  fontSize: 10.5, fontWeight: 600,
                }}>
                  <span style={{ letterSpacing: 2, fontSize: 9 }}>{r.symbol}</span>
                  {r.label} ({r.min}–{r.max}%)
                </span>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: '#000000', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
              Controls
            </div>
            <div style={{ display: 'flex', gap: '6px 20px', flexWrap: 'wrap' }}>
              {[
                ['Click + drag', 'Select a base range'],
                ['Copy button',  'Copy selected bases to clipboard'],
                ['Jump to pos',  'Scroll viewer to a specific position'],
                ['Bases/row',    'Change how many bases appear per row'],
                ['Zoom S/M/L',   'Adjust cell size (small / medium / large)'],
                ['Conservation', 'Switch between binary, bar, and heatmap tracks'],
                ['Theme',        'Standard vs. color-blind-friendly palette'],
              ].map(([key, desc]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                  <kbd style={{
                    background: '#f1f5f9', border: '1px solid #e2e8f0',
                    borderRadius: 3, padding: '1px 6px',
                    fontFamily: "'Courier New', monospace", fontSize: 10,
                    whiteSpace: 'nowrap',
                  }}>{key}</kbd>
                  <span style={{ color: '#000000' }}>{desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const SequenceAlignmentViz = ({
  alignments,
  onSabinMatchFound,
  onSimilarityRangeFound,
  onPrev,
  onNext,
  alignmentIndex,
  totalAlignments,
}) => {
  const [sabinResults, setSabinResults] = useState(null);
  const [sabinLoading, setSabinLoading] = useState(true);
  const [colorTheme,   setColorTheme]   = useState('standard');
  const [vp1Region,    setVp1Region]    = useState(null);

  if (!alignments?.qseq || !alignments?.sseq) {
    return (
      <div style={{
        padding: 16, background: '#fef2f2', border: '1px solid #fca5a5',
        borderRadius: 8, color: '#991b1b', fontSize: 12, fontFamily: 'sans-serif',
      }}>
        Missing sequence data — qseq and sseq are required.
      </div>
    );
  }

  useEffect(() => {
    setSabinLoading(true);
    setSabinResults(null);
    setVp1Region(null);
    try {
      const results = compareWithSabin(alignments.qseq, SABIN_STRAINS);
      setSabinResults(results);
      if (results.length > 0) {
        const best = results[0];
        onSimilarityRangeFound?.({
          similarity:  best.similarity,
          strain:      best.strain,
          position:    best.position,
          range:       best.rangeInfo?.label,
          isHighMatch: best.similarity > 95,
        });
        if (best.similarity > 95) onSabinMatchFound?.(best);
      }
    } catch (err) {
      console.error('Sabin similarity analysis error:', err);
    } finally {
      setSabinLoading(false);
    }
  }, [alignments]);

  const pident = parseFloat(alignments.pident ?? 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontFamily: 'sans-serif' }}>

      {/* ── Alignment header ── */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
        {/* Dark title bar */}
        <div style={{
          padding: '9px 14px', background: '#fbfbfb',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
        }}>
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <div style={{ fontFamily: "'Courier New', monospace", fontSize: 11, color: '#000000', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <span style={{ color: '#000000' }}>Query: </span>
              <span style={{ color: '#000000', fontWeight: 600 }}>{alignments.qseqid || '—'}</span>
            </div>
            <div style={{ fontFamily: "'Courier New', monospace", fontSize: 11, color: '#000000', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>
              <span style={{ color: '#000000' }}>Subject: </span>
              <span style={{ color: '#000000', fontWeight: 600 }}>{alignments.sseqid || '—'}</span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
            <StatusBadge similarity={pident} />

            {/* Color theme */}
            <div style={{ display: 'flex', gap: 3 }}>
              {[['standard', 'Std'], ['colorblind', 'CB-safe']].map(([k, l]) => (
                <button key={k} onClick={() => setColorTheme(k)} style={{
                  padding: '2px 7px', fontSize: 9.5, borderRadius: 4,
                  border: `1px solid ${colorTheme === k ? 'rgba(255,255,255,.5)' : 'rgba(255,255,255,.15)'}`,
                  background: colorTheme === k ? 'rgba(255,255,255,.2)' : 'rgba(255,255,255,.06)',
                  color: colorTheme === k ? '#fff' : '#000000',
                  cursor: 'pointer', fontWeight: colorTheme === k ? 700 : 400,
                }}>{l}</button>
              ))}
            </div>

            {/* Prev / Next */}
            {(onPrev || onNext) && (
              <div style={{ display: 'flex', gap: 3 }}>
                <button onClick={onPrev} disabled={!onPrev} style={{
                  padding: '3px 9px', fontSize: 11, borderRadius: 4,
                  border: '1px solid rgba(255,255,255,.2)', background: 'rgba(255,255,255,.08)',
                  color: '#e2e8f0', cursor: onPrev ? 'pointer' : 'default', opacity: onPrev ? 1 : 0.35,
                }}>‹ Prev</button>
                <button onClick={onNext} disabled={!onNext} style={{
                  padding: '3px 9px', fontSize: 11, borderRadius: 4,
                  border: '1px solid rgba(255,255,255,.2)', background: 'rgba(255,255,255,.08)',
                  color: '#e2e8f0', cursor: onNext ? 'pointer' : 'default', opacity: onNext ? 1 : 0.35,
                }}>Next ›</button>
              </div>
            )}
          </div>
        </div>

        {/* BLAST metadata */}
        <div style={{ padding: 10 }}>
          <MetaGrid alignments={alignments} />
        </div>
      </div>

      {/* ── Sabin reference card ── */}
      <SabinCard
        results={sabinResults}
        loading={sabinLoading}
        qseqLen={alignments.qseq.length}
        onViewPosition={(region) => {
          setVp1Region(region);
          // Scroll the viewer into view after a tick so the DOM updates first
          setTimeout(() => {
            document.getElementById('sa-chunk-0')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 50);
        }}
      />

      {/* ── Sequence viewer ── */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{
          padding: '8px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#000000', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Sequence Alignment Viewer · {alignments.qseq.length} bp
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, color: '#000000' }}>
              Click + drag to select bases · Hover for position details
            </span>
            {vp1Region && (
              <button
                onClick={() => setVp1Region(null)}
                style={{
                  padding: '2px 9px', fontSize: 10, fontWeight: 600, borderRadius: 4,
                  border: '1px dashed #f59e0b', background: '#fef3c7',
                  color: '#92400e', cursor: 'pointer',
                }}
              >
                ✕ Clear VP1 highlight
              </button>
            )}
          </div>
        </div>
        <div style={{ padding: '10px 14px' }}>
          <SequenceViewer
            qseq={alignments.qseq}
            sseq={alignments.sseq}
            colorTheme={colorTheme}
            vp1Region={vp1Region}
          />
        </div>
      </div>

      {/* ── Info panel ── */}
      <InfoPanel />
    </div>
  );
};

export default SequenceAlignmentViz;
